import { TypographyV3 } from '@jouzen/ecom-components';
import { cx } from 'class-variance-authority';
import { AnimatePresence } from 'framer-motion';
import { useTranslations } from 'next-intl';

import Motion from '@/app/components/Motion';

import type { LandingPageFeature } from './types';

export interface FeaturesTabsProps {
  readonly activeFeatureIndex: number;
  readonly features: LandingPageFeature[];
  readonly onTabClick: (index: number) => void;
}

const FeaturesTabs = ({
  activeFeatureIndex,
  features,
  onTabClick,
}: FeaturesTabsProps): JSX.Element => {
  const t = useTranslations();
  return (
    <ul className="flex flex-col gap-y-6">
      <AnimatePresence>
        {features.map((feature, i) => (
          <li key={feature.id}>
            <Motion
              className="h-full"
              initial={{ opacity: 0, x: -50 }}
              transition={{ delay: (i + 1) * 0.2, duration: 0.5 }}
              viewport={{ once: true }}
              whileInView={{ opacity: 1, x: 0 }}
            >
              <button
                className={cx('transition-opacity duration-300', {
                  'opacity-50': activeFeatureIndex !== i,
                  'opacity-100': activeFeatureIndex === i,
                })}
                data-cy="landing-page-feature-tab"
                onClick={() => onTabClick(i)}
                type="button"
              >
                <TypographyV3
                  className="font-normal tracking-tighter"
                  color="inherit"
                  Element="h3"
                  variant="h4"
                >
                  {t(feature.title)}
                </TypographyV3>
              </button>
              {activeFeatureIndex === i && (
                <Motion
                  key={feature.id}
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -50 }}
                  transition={{ delay: 0.25, duration: 0.5 }}
                >
                  <TypographyV3
                    Element="p"
                    color="inherit"
                    className="mt-4 max-w-[450px] text-body-sm"
                    data-cy="landing-page-feature-description"
                  >
                    {t(feature.description)}
                  </TypographyV3>
                </Motion>
              )}
            </Motion>
          </li>
        ))}
      </AnimatePresence>
    </ul>
  );
};

export default FeaturesTabs;
